/**
 * Workshop EventFrame — iOS-style upcoming-event card.
 *
 * Native-source note: no official OSS component exists for the Apple
 * Calendar date badge, so the badge is recreated pinned to spec (red
 * month label over a big day numeral, rounded like the Calendar icon).
 *
 * Row recipe copied from taskboard list-view-row (title
 * text-appkit-caption, muted detail text-canvas-xs) with the closest
 * production fill bg-elevated/60 for the single glass row. Denker glass
 * outside. No FrameHeader/FrameFooter — hover pill only.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  Spinner,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface EventFrameProps {
  month?: string;
  day?: string;
  title?: string;
  detail?: string;
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

const MOCK_EVENT = {
  month: "OCT",
  day: "22",
  title: "Keynote — Designing with AI",
  detail: "Wednesday · 14:30 – 14:50 · Config, Berlin",
};

export function EventFrame({
  month = MOCK_EVENT.month,
  day = MOCK_EVENT.day,
  title = MOCK_EVENT.title,
  detail = MOCK_EVENT.detail,
  status = "done",
  className,
  "data-testid": testId = "event-frame",
}: EventFrameProps) {
  const showStreamingCaret = status === "streaming";
  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex w-[320px] flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div
        className="nodrag flex flex-1 flex-col gap-2.5 px-3 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        {status === "error" ? (
          <div
            className="flex items-center gap-2 rounded-preview bg-danger-muted px-3 py-4"
            data-testid={`${testId}-error`}
            role="alert"
          >
            <Icons.CircleAlert
              className="h-3.5 w-3.5 shrink-0 text-status-error"
              aria-hidden="true"
            />
            <span className="text-canvas-sm text-secondary">
              Couldn&apos;t load this event. Try again.
            </span>
          </div>
        ) : status === "streaming" ? (
          <div
            className="flex items-center gap-3 rounded-preview bg-elevated/60 px-3 py-2.5"
            data-testid={`${testId}-loading`}
            aria-label="Loading event"
          >
            <div
              className="h-12 w-12 shrink-0 animate-pulse rounded-preview bg-glass-fill-dense"
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5" aria-hidden="true">
              <div className="h-3 w-3/4 animate-pulse rounded bg-glass-fill-dense" />
              <div className="h-2.5 w-1/2 animate-pulse rounded bg-glass-fill-dense" />
            </div>
            <Spinner className="h-3 w-3 shrink-0" />
          </div>
        ) : !title ? (
          <div
            className="flex flex-col items-center gap-1.5 px-3 py-6 text-center"
            data-testid={`${testId}-empty`}
          >
            <Icons.Calendar
              className="h-4 w-4 text-muted"
              aria-hidden="true"
            />
            <p className="text-canvas-sm italic text-muted">
              No upcoming event
            </p>
          </div>
        ) : (
          <div
            className="flex items-center gap-3 rounded-preview bg-elevated/60 px-3 py-2.5"
            data-testid={`${testId}-row`}
          >
            <div
              className="flex h-14 w-12 shrink-0 flex-col items-center justify-center overflow-hidden rounded-preview bg-glass-fill-dense"
              data-testid={`${testId}-badge`}
              aria-label={`${month} ${day}`}
            >
              <span className="text-canvas-2xs font-bold uppercase tracking-wide text-danger">
                {month}
              </span>
              <span className="text-canvas-xl font-bold tabular-nums leading-none text-primary">
                {day}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-appkit-caption font-semibold text-primary"
                data-testid={`${testId}-title`}
              >
                {title}
                {showStreamingCaret && (
                  <span
                    className="ml-1 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
                    aria-label="Streaming"
                  />
                )}
              </p>
              {detail && (
                <p
                  className="mt-0.5 truncate text-canvas-xs text-muted"
                  data-testid={`${testId}-detail`}
                >
                  {detail}
                </p>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export type { EventFrameProps };
